import React, { createContext, useReducer, useRef, ReactNode } from 'react';
import Reducer from './Reducer';
import { ChallengeContext } from './challenge-context';

export const REG_TYPES = {
	STANDARD: 'standard',
	IPP: 'ipp',
} as const;

export const RACE_TYPES = {
	ELVES: 'elves',
	HUMANS: 'humans',
} as const;

type RegType = typeof REG_TYPES[keyof typeof REG_TYPES];
type RaceType = typeof RACE_TYPES[keyof typeof RACE_TYPES] | null;

type Action =
	| { type: string }
	| { type: string; payload: unknown }
	| { type: string; data: unknown };

interface State {
	race: RaceType;
	showRaceSelection: boolean;
	currentStep: number;
	showCookieOverlay: boolean;
	regType: RegType;
	thirdPartyTrackingAccepted: boolean;
	trackingExecuted: boolean;
	backgroundMusicPlaying: boolean;
	agesPerStep: string[];
	premiumPerStep: number[];
	showServerError: boolean;
	serverErrorMessage: string;
	showServerSuccess: boolean;
	serverSuccessMessage: string;
	portalBarInitialized: boolean;
	portalBarHTMLLoaded: boolean;
	showRememberMeModal: boolean;
	loginInputsDisabled: boolean;
	rememberMe: boolean;
	translationsLoaded: boolean;
	showForgotPWOverlay: boolean;
	serverMessages: string[];
	language: string;
	privacyUrl: string;
	termsAndConditionsUrl: string;
	imprintUrl: string;
	locale: string;
	ippLink: string;
	showLoginHubFormSuccess: boolean;
	playerId?: number;
	lpsToken?: string;
	preLanderStep: number;
	preLanderCompleted: boolean;
	oneTimeLoginUrl: string|undefined;
}

const initialState: State = {
	race: null,
	showRaceSelection: false,
	currentStep: 1,
	showCookieOverlay: false,
	regType: REG_TYPES.STANDARD,
	thirdPartyTrackingAccepted: false,
	trackingExecuted: false,
	backgroundMusicPlaying: false,
	agesPerStep: ['Stone Age', 'Middle Age', 'Industrial Age'],
	premiumPerStep: [20, 20, 60],
	showServerError: false,
	serverErrorMessage: '',
	showServerSuccess: false,
	serverSuccessMessage: '',
	portalBarInitialized: false,
	portalBarHTMLLoaded: false,
	showRememberMeModal: false,
	loginInputsDisabled: true,
	rememberMe: true,
	translationsLoaded: false,
	showForgotPWOverlay: false,
	serverMessages: [],
	language: '',
	privacyUrl: '',
	termsAndConditionsUrl: '',
	imprintUrl: '',
	locale: '',
	ippLink: '',
	showLoginHubFormSuccess: false,
	playerId: undefined,
	lpsToken: undefined,
	preLanderStep: 1,
	preLanderCompleted: false,
	oneTimeLoginUrl: undefined,
};

export const AppContext = createContext<[State, React.Dispatch<Action>]>([initialState, () => null]);


interface StoreProps {
	children: ReactNode;
}

const Store: React.FC<StoreProps> = ({ children }) => {
	const [state, dispatch] = useReducer(Reducer, initialState);
	const challengePromiseRef = useRef<Promise<string> | null>(null);
	return (
		<AppContext.Provider value={[state, dispatch]}>
			<ChallengeContext.Provider value={challengePromiseRef}>
				{children}
			</ChallengeContext.Provider>
		</AppContext.Provider>
	);
};

export default Store;
